import PropTypes from 'prop-types';
import { Button, Dialog } from '@etg/wings';
import { DangerousHtmlContent } from '@eti/components';
import { css, mediaQueries } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import { CONFIRMATION_MODAL_REDIRECT_TO, CONFIRMATION_MODAL_TYPE, CONFIRMATION_MODAL_ACTION_TYPE } from '../../utils/constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const backdropStyles = css`
  background: rgb(101 101 101 / 80%);
  height: 100%;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 11;
`;
const textStyles = isHtml => css`
  font-size: 0.875rem;
  margin: 12px 0;
  ${!isHtml && `font-weight: 700`};
`;
const segmentsStyles = css`
  list-style: disc;
  margin: 12px 10px;
  padding-left: 14px;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 14px;
  }
`;
const segmentStyles = css`
  font-size: 0.75rem;
`;
const iataStyles = css`
  font-weight: 700;
`;
const dividerStyles = css`
  margin: 0 2px;
`;
const buttonsWrapperStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 5px;

  @media ${mediaQueries.large.up} {
    flex-direction: row;
  }
`;
const buttonStyles = css`
  flex: 1;
`;
const generateKey = (from, to, i) => `${from}-${to}-${i}`;
const SeatMapConfirmation = _ref => {
  let {
    moveToExtraProductsPage,
    onSelection,
    segments,
    redirectTo = CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT,
    type = CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS
  } = _ref;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const title = t('Product.SeatMap.AllSeatsSelectedConfirmation.Title');
  const text = t('Product.SeatMap.AllSeatsSelectedConfirmation.Text');
  const dismissText = () => {
    switch (redirectTo) {
      case CONFIRMATION_MODAL_REDIRECT_TO.PAYMENT:
        return t('Product.SeatMap.AllSeatsSelectedConfirmation.Payment.DismissText');
      case CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY:
        return t('Product.SeatMap.AllSeatsSelectedConfirmation.Summary.DismissText');
      case CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT:
      default:
        return t('Product.SeatMap.AllSeatsSelectedConfirmation.DismissText');
    }
  };
  const onDismissPersistModal = () => {
    if (p('TravelerDetails.DynamicFlow.Enabled')) {
      moveToExtraProductsPage();
    } else {
      onSelection(CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_DISMISS);
    }
  };
  const acceptText = t('Product.SeatMap.AllSeatsSelectedConfirmation.AcceptText');
  const getConfirmationContent = () => {
    const segmentIndex = segments.length > 0 ? segments[0].boundSegmentIndex : null;
    switch (type) {
      case CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT:
        return /*#__PURE__*/_jsxs(Dialog, {
          closeButtonAriaLabel: t('ScreenReader.CloseButtonDialog.AriaLabel.Text'),
          isOpen: true,
          title: /*#__PURE__*/_jsx(DangerousHtmlContent, {
            content: t('Product.SeatMap.Persist.Modal.Title')
          }),
          children: [/*#__PURE__*/_jsx("div", {
            className: textStyles(true),
            children: /*#__PURE__*/_jsx(DangerousHtmlContent, {
              content: t('Product.SeatMap.Persist.Modal.Text')
            })
          }), /*#__PURE__*/_jsxs("div", {
            className: buttonsWrapperStyles,
            children: [/*#__PURE__*/_jsx(Button, {
              className: buttonStyles,
              "data-testid": "seatMap-persist-modal-dismiss",
              onClick: onDismissPersistModal,
              children: t('Product.SeatMap.Persist.Modal.Actions.Accept')
            }), /*#__PURE__*/_jsx(Button, {
              className: buttonStyles,
              "data-testid": "seatMap-persist-modal-accept",
              onClick: () => onSelection(CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_ACCEPT, segmentIndex),
              variant: "primary",
              children: t('Product.SeatMap.Persist.Modal.Actions.Dismiss')
            })]
          })]
        });
      case CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS:
      default:
        return /*#__PURE__*/_jsxs(Dialog, {
          closeButtonAriaLabel: t('ScreenReader.CloseButtonDialog.AriaLabel.Text'),
          isOpen: true,
          title: title,
          children: [/*#__PURE__*/_jsx("div", {
            className: textStyles(false),
            children: text
          }), /*#__PURE__*/_jsx("ul", {
            className: segmentsStyles,
            children: segments.map((_ref2, i) => {
              let {
                from,
                to
              } = _ref2;
              return /*#__PURE__*/_jsxs("li", {
                className: segmentStyles,
                children: [from.city, " ", /*#__PURE__*/_jsx("span", {
                  className: iataStyles,
                  children: from.iata
                }), /*#__PURE__*/_jsx("span", {
                  className: dividerStyles,
                  children: "\u2013"
                }), to.city, " ", /*#__PURE__*/_jsx("span", {
                  className: iataStyles,
                  children: to.iata
                })]
              }, generateKey(from, to, i));
            })
          }), /*#__PURE__*/_jsxs("div", {
            className: buttonsWrapperStyles,
            children: [/*#__PURE__*/_jsx(Button, {
              className: buttonStyles,
              "data-testid": "seatMap-allSeatsSelectedConfirmation-dismiss",
              onClick: () => onSelection(CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_DISMISS),
              children: dismissText()
            }), /*#__PURE__*/_jsx(Button, {
              className: buttonStyles,
              "data-testid": "seatMap-allSeatsSelectedConfirmation-accept",
              onClick: () => onSelection(CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_ACCEPT, segmentIndex),
              variant: "primary",
              children: acceptText
            })]
          })]
        });
    }
  };
  return /*#__PURE__*/_jsx("div", {
    className: backdropStyles,
    "data-testid": `seatMap-persist-modal-${type}`,
    children: getConfirmationContent()
  });
};
SeatMapConfirmation.propTypes = {
  moveToExtraProductsPage: PropTypes.func,
  onSelection: PropTypes.func,
  redirectTo: PropTypes.oneOf(Object.values(CONFIRMATION_MODAL_REDIRECT_TO)),
  segments: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  type: PropTypes.oneOf(Object.values(CONFIRMATION_MODAL_TYPE))
};
export default SeatMapConfirmation;
//# sourceMappingURL=SeatMapConfirmation.js.map